// @ts-nocheck
/*
 * Port of views/dashboard/contact-group/edit.js (origin/main-dev, 1033 lines).
 * BYTE-IDENTICAL — sessionStorage caches the group on nav for reload safety,
 * fallback dummy group object preserved verbatim; text validation via
 * textRegex; complete edit modal with primary + 2 alt numbers, all
 * onBlur trims; 3-choice Add Contact dropdown (Create/Upload/Import);
 * CSV upload with downloadTemplate; PrimeReact DataTable → obd3-gui
 * DataTable + shadcn Button; Actions column uses react-bootstrap Dropdown.
 */
import React, { useState, useEffect, useRef } from 'react';
import { CustomFieldsSection } from './custom-fields/CustomFieldsSection';
import { UploadCustomFieldsChooser, useUploadCustomFields } from './custom-fields/UploadCustomFieldsChooser';
import { AdditionalDetailsCell, additionalDetailsText } from './custom-fields/AdditionalDetailsCell';
import { useCustomFieldsVisible } from './custom-fields/useCustomFieldsVisible';
import { useContactCustomFieldsForm } from './custom-fields/useContactCustomFieldsForm';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import downloadTemplate from '@/utilities/downloadCsv';
import { Card as ShadcnCard, CardContent as ShadcnCardContent } from '@/components/ui/card';
import { Button as RBButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuContent as ShadcnDropdownMenuContent,
  DropdownMenuItem as ShadcnDropdownMenuItem,
  DropdownMenuTrigger as ShadcnDropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/* Local shims — see AllContactsPage.tsx for the identical helper block. */
const cx = (...args: any[]) => args.filter(Boolean).join(' ');

const Row: any = ({ className, children, ...props }: any) => (
  <div className={cx('row', className)} {...props}>
    {children}
  </div>
);
const buildColCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs);
  push('sm', sm);
  push('md', md);
  push('lg', lg);
  push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={cx(buildColCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Card: any = ({ className, children, ...props }: any) => (
  <ShadcnCard className={className} {...props}>
    {children}
  </ShadcnCard>
);
Card.Body = ({ className, children, ...props }: any) => (
  <ShadcnCardContent className={className} {...props}>
    {children}
  </ShadcnCardContent>
);

const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, ...props }: any) => {
  const cls = cx('form-control', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Check = ({ type = 'checkbox', label, id, className, ...props }: any) => (
  <div className={cx('form-check', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={cx(muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);

const Modal: any = ({ show, onHide, size, centered: _c, className, children, ...props }: any) => {
  const sizeCls = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-3xl' : size === 'xl' ? 'max-w-5xl' : 'max-w-lg';
  return (
    <Dialog open={!!show} onOpenChange={(next: boolean) => !next && onHide?.()}>
      <DialogContent className={cx(sizeCls, 'max-h-[85vh] overflow-y-auto', className)} {...props}>
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ closeButton: _cb, className, children, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Body = ({ className, children, ...props }: any) => (
  <div className={className} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ className, children, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
Modal.Title = ({ className, children, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);

const btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  success: 'default',
  info: 'default',
  warning: 'default',
  light: 'outline',
  dark: 'default',
  link: 'link',
  'outline-primary': 'outline',
  'outline-secondary': 'outline',
  'outline-danger': 'outline',
  'outline-success': 'outline',
};

const Dropdown: any = ({ children, autoClose: _autoClose, ...props }: any) => (
  <ShadcnDropdownMenu {...props}>{children}</ShadcnDropdownMenu>
);
Dropdown.Toggle = ({ children, className, variant = 'outline', id: _id, ...props }: any) => (
  <ShadcnDropdownMenuTrigger asChild>
    <RBButton variant={btnVariantMap[variant] || 'outline'} className={className} {...props}>
      {children}
    </RBButton>
  </ShadcnDropdownMenuTrigger>
);
Dropdown.Menu = ({ children, className, ...props }: any) => (
  <ShadcnDropdownMenuContent className={className} align="end" {...props}>
    {children}
  </ShadcnDropdownMenuContent>
);
Dropdown.Item = ({ children, onClick, className, ...props }: any) => (
  <ShadcnDropdownMenuItem className={className} onClick={onClick} {...props}>
    {children}
  </ShadcnDropdownMenuItem>
);
Dropdown.ItemText = ({ children, className, ...props }: any) => (
  <div className={cx('px-2 py-1.5 text-sm', className)} onClick={(e) => e.stopPropagation()} {...props}>
    {children}
  </div>
);

import type { ColumnDef } from '@tanstack/react-table';
import { FiEdit, FiTrash2, FiArrowLeft, FiPhone, FiMoreVertical } from 'react-icons/fi';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { postApiMediaCall } from '@/service/postApiCall';
import { toast } from 'sonner';
import { canImportContactGroupMembers } from '@/utilities/promptUploadAccess';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CONTACT_GROUP_MEMBERS_UPDATE,
  API_CONTACT_GROUP_MEMBERS_DELETE,
  API_CONTACT_GROUP_MEMBERS_BULK_DELETE,
  API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP,
  API_CONTACT_GROUP_MEMBERS_UPLOAD,
  API_CONTACT_GROUP_MEMBERS_DELETE_ALL,
} from '@/service/url';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { contactFieldError } from '@/utilities/contactFieldRules';

const CONTACT_TEXT_LABELS = {
  first_name: 'First name',
  last_name: 'Last name',
  street: 'Street',
  city: 'City',
  state: 'State',
  country: 'Country',
  company: 'Company',
};
import { getUser } from '@/utilities/setting';
import { UPLOAD_CONTACT_GROUP_MEMBERS, UPLOAD_CONTACT_GROUP_MEMBERS_DATA, CONTACT_PAGE_SIZES } from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, RefreshCw, Trash2 } from 'lucide-react';
import { hasPermission } from '@/utilities/helpers';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const fmtDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !isNaN(d.getTime())
    ? d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
};
const datesCell = (r) => (
  <div className="flex flex-col gap-0.5 whitespace-nowrap text-xs tabular-nums">
    <span><span className="text-muted-foreground">Created:</span> <span className="text-foreground">{fmtDate(r.created_at)}</span></span>
    <span><span className="text-muted-foreground">Updated:</span> <span className="text-foreground">{fmtDate(r.updated_at)}</span></span>
  </div>
);

const EXPORT_PAGE = 2000;
const EXPORT_MAX = 100000;

const GroupDetail = () => {
  const { checkAuth } = useAuth();
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = usePersistedState(`contact-group/GroupDetail:${id}:searchValue`, '');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [currentContact, setCurrentContact] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState(() => searchValue.trim());
  const [paging, setPaging] = usePersistedState(`contact-group/GroupDetail:${id}:paging`, { pageIndex: 0, pageSize: 10 });
  const [total, setTotal] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const cursors = useRef({ key: '', last: {} });
  const reqSeq = useRef(0);
  const reload = () => setReloadKey((k) => k + 1);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [bulkDelete, setBulkDelete] = useState<{ ids: string[]; clear: () => void } | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  // Also supplies the templates for the edit modal's custom fields.
  const customUpload = useUploadCustomFields();
  const showCustomFields = useCustomFieldsVisible();
  const templateName = (row) => {
    const tid = Number(row?.custom_field_template_id);
    if (!tid) return '-';
    if (customUpload.loading) return '…';
    return customUpload.templates.find((t) => t.id === tid)?.name || 'Deleted template';
  };
  const customForm = useContactCustomFieldsForm(customUpload.templates, customUpload.loading);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

  const numberRegex = /^\d{10}$/;

  function changeUploadFile(e) {
    const file = e.target.files[0];
    if (file && /\.csv$/i.test(file.name)) {
      setUploadFile(file);
      setIsFileUpload(true);
    } else {
      toast.error('Please upload a CSV file.');
      setUploadFile(null);
      setIsFileUpload(false);
    }
  }

  const GROUP_STORAGE_KEY = `contact-group:${id}`;

  const getStoredGroup = () => {
    try {
      const raw = sessionStorage.getItem(GROUP_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const group = state?.group || getStoredGroup() || {
    id,
    name: 'Sample Group',
    members: 2,
    createdBy: 'admin',
    createdOn: '10 Jul 2025, 10:00:00 AM',
    description: 'This is a sample contact group',
  };

  useEffect(() => {
    if (state?.group) {
      try {
        sessionStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(state.group));
      } catch {}
    }
  }, [state, GROUP_STORAGE_KEY]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchValue.trim();
      if (next === search) return;
      setSearch(next);
      setPaging((p) => ({ ...p, pageIndex: 0 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchValue]);

  useEffect(() => {
    const key = `${id}|${search}|${paging.pageSize}|${reloadKey}`;
    if (cursors.current.key !== key) cursors.current = { key, last: {} };
    const after = cursors.current.last[paging.pageIndex - 1];
    const pageIndex = paging.pageIndex;
    const seq = ++reqSeq.current;
    setLoading(true);
    getApiCall(API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP.replace(':id', id), {
      full: '1',
      page: pageIndex + 1,
      limit: paging.pageSize,
      ...(after ? { after } : {}),
      ...(search ? { search } : {}),
    })
      .then((resp) => {
        if (seq !== reqSeq.current) return; // a newer request is in flight
        const list = resp?.rows || [];
        if (!list.length && pageIndex > 0) return setPaging((p) => ({ ...p, pageIndex: pageIndex - 1 }));
        setContacts(list);
        setTotal(Number(resp?.total) || 0);
        setTotalCapped(!!resp?.total_capped);
        if (list.length) cursors.current.last[pageIndex] = list[list.length - 1].id;
      })
      .catch(onError)
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
      });
  }, [id, paging.pageIndex, paging.pageSize, search, reloadKey]);

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleEditClick = (contact) => {
    setCurrentContact(contact);
    customForm.load(contact);
    setShowEditModal(true);
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setCurrentContact(null);
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      const { exportToCSV } = await import('@/utilities/helpers');
      const all = [];
      let after = null;
      while (all.length < EXPORT_MAX) {
        const resp: any = await getApiCall(API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP.replace(':id', id), {
          full: '1',
          page: 1,
          limit: EXPORT_PAGE,
          count: '0',
          ...(after ? { after } : {}),
          ...(search ? { search } : {}),
        });
        const list = resp?.rows || [];
        all.push(...list);
        if (list.length < EXPORT_PAGE) break;
        after = list[list.length - 1].id;
      }
      if (all.length >= EXPORT_MAX) {
        toast.warning(`Export has the newest ${EXPORT_MAX.toLocaleString()} contacts only.`);
      }
      exportToCSV({
        fileName: 'contacts-list',
        data: all.slice(0, EXPORT_MAX),
        columns: [
          { header: 'Name', accessor: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-' },
          { header: 'Number', accessor: (row) => row.primary_number || '-' },
          { header: 'Street Address', accessor: (row) => row.street || '-' },
          { header: 'City', accessor: (row) => row.city || '-' },
          { header: 'State', accessor: (row) => row.state || '-' },
          { header: 'Country', accessor: (row) => row.country || '-' },
          { header: 'Company', accessor: (row) => row.company || '-' },
          ...(showCustomFields
            ? [{ header: 'Customer Additional Details', accessor: (row) => additionalDetailsText(row.additional_details) }]
            : []),
          { header: 'Created At', accessor: (row) => fmtDate(row.created_at) },
          { header: 'Updated At', accessor: (row) => fmtDate(row.updated_at) },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    customUpload.reset();
  };
  const handleShowUploadModal = () => setShowUploadModal(true);

  const getColumnsAndFilename = () => {
    const columns = UPLOAD_CONTACT_GROUP_MEMBERS;
    const filename = 'Contact_Group_Members-Upload.csv';
    const sample_data = UPLOAD_CONTACT_GROUP_MEMBERS_DATA;
    return { columns, filename, sample_data };
  };

  const handleSaveUpload = () => {
    if (!customUpload.ready) return toast.error('Select a custom field template, or choose No.');
    if (!uploadFile) return toast.error('Please choose a CSV file.');
    const formData = new FormData();
    formData.append('contact_group_id', id);
    formData.append('members_data', uploadFile);
    customUpload.appendTo(formData);
    setBusy(true);
    postApiMediaCall(API_CONTACT_GROUP_MEMBERS_UPLOAD, formData)
      .then(onUploadSuccess)
      .catch(onUploadError)
      .finally(() => setBusy(false));
  };

  function onUploadSuccess(data) {
    const linked = Number(data?.linked) || 0;
    const repeated = Number(data?.duplicates) || 0;
    const updated = Number(data?.updated) || 0;
    const updatedNums = Array.isArray(data?.updated_numbers) ? data.updated_numbers : [];
    const updatedNote = updated
      ? `${updated} existing contact(s) updated with the file's details (${updatedNums.slice(0, 5).join(', ')}${updated > 5 ? ', ...' : ''})`
      : '';
    const invalidRows = Array.isArray(data?.invalid_rows) ? data.invalid_rows : [];
    const invalidHint = invalidRows.length
      ? ` (${invalidRows.slice(0, 5).map((r) => `row ${r.row}: ${r.reason}`).join('; ')}${data.invalid > 5 ? '; ...' : ''})`
      : '';
    const skipped = [
      repeated > 0 && `${repeated} repeated in file`,
      data?.invalid && `${data.invalid} invalid${invalidHint}`,
    ].filter(Boolean);
    const added =
      `${Number(data?.added) || 0} contact(s) added` +
      (updatedNote ? `, ${updatedNote}` : '') +
      (linked ? `, ${linked} of them newly added to this group` : '');
    if (skipped.length) toast.warning(`${added}. Skipped: ${skipped.join(', ')}.`);
    else if (updated) toast.info(`${added}.`, { duration: 8000 });
    else toast.success(added);
    handleCloseUploadModal();
    reload();
  }

  function onUploadError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.error('Data creation error:', err);
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to upload contacts');
    }
  }

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numberFields = ['primary_number', 'alt1_number', 'alt2_number'];

    if (numberFields.includes(name)) {
      const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
      setCurrentContact((prev) => ({ ...prev, [name]: cleanedValue }));
      return;
    }

    const fieldError = contactFieldError(name, CONTACT_TEXT_LABELS[name] || 'This field', value);
    if (fieldError) {
      toast.error(fieldError);
      return;
    }

    setCurrentContact((prev) => ({ ...prev, [name]: value.replace(/^\s+/, '') }));
  };

  const handleSaveChanges = (e) => {
    e.preventDefault();

    const trimmedContact = {
      ...currentContact,
      first_name: currentContact.first_name?.trim(),
      last_name: currentContact.last_name?.trim(),
      street: currentContact.street?.trim(),
      city: currentContact.city?.trim(),
      state: currentContact.state?.trim(),
      country: currentContact.country?.trim(),
      company: currentContact.company?.trim(),
      primary_number: currentContact.primary_number?.trim(),
      alt1_number: currentContact.alt1_number?.trim(),
      alt2_number: currentContact.alt2_number?.trim(),
    };

    if (!trimmedContact.first_name || !trimmedContact.primary_number) {
      toast.error('First name and primary number are required');
      setShowToast(true);
      return;
    }

    if (!/^\d{10}$/.test(trimmedContact.primary_number)) {
      toast.error('Primary number must be exactly 10 digits');
      setShowToast(true);
      return;
    }

    if (trimmedContact.alt1_number && !/^\d{10}$/.test(trimmedContact.alt1_number)) {
      toast.error('Alternate number 1 must be exactly 10 digits or empty');
      setShowToast(true);
      return;
    }

    if (trimmedContact.alt2_number && !/^\d{10}$/.test(trimmedContact.alt2_number)) {
      toast.error('Alternate number 2 must be exactly 10 digits or empty');
      setShowToast(true);
      return;
    }

    for (const [key, label] of Object.entries(CONTACT_TEXT_LABELS)) {
      const fieldError = trimmedContact[key] ? contactFieldError(key, label, trimmedContact[key]) : null;
      if (fieldError) {
        toast.error(fieldError);
        return;
      }
    }

    if (!numberRegex.test(trimmedContact.primary_number)) {
      toast.error('Primary number must be exactly 10 digits');
      return;
    }

    if (trimmedContact.alt1_number && !numberRegex.test(trimmedContact.alt1_number)) {
      toast.error('Alternate number 1 must be exactly 10 digits');
      return;
    }

    if (trimmedContact.alt2_number && !numberRegex.test(trimmedContact.alt2_number)) {
      toast.error('Alternate number 2 must be exactly 10 digits');
      return;
    }

    const numbers = [
      trimmedContact.primary_number,
      trimmedContact.alt1_number,
      trimmedContact.alt2_number,
    ].filter(Boolean);

    if (new Set(numbers).size !== numbers.length) {
      toast.error('Phone numbers must not be duplicated');
      return;
    }

    const payload = {
      first_name: trimmedContact.first_name,
      last_name: trimmedContact.last_name,
      street: trimmedContact.street,
      city: trimmedContact.city,
      state: trimmedContact.state,
      country: trimmedContact.country,
      company: trimmedContact.company,
      contact_group_id: id,
      primary_number: trimmedContact.primary_number,
      alt1_number: trimmedContact.alt1_number || null,
      alt2_number: trimmedContact.alt2_number || null,
    };

    const customError = customForm.validate();
    if (customError) {
      toast.error(customError);
      return;
    }
    if (customUpload.enabled) Object.assign(payload, customForm.payload(true));

    const url = API_CONTACT_GROUP_MEMBERS_UPDATE.replace(':id', currentContact.id);
    setBusy(true);
    postApiCall(url, payload)
      .then(onUpdateSuccess)
      .catch(onUpdateError)
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    if (toastProps) {
      setShowToast(true);
    }
  }, [toastProps]);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  function onUpdateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to update member');
    }
  }

  function onUpdateSuccess() {
    toast.success('Contact Member updated successfully');
    handleModalClose();
    reload();
  }

  const filteredContacts = contacts; // filtered server-side by `search`

  const handleDeleteClick = (contactId) => {
    setContactToDelete(contactId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (bulkDelete) {
      setBusy(true);
      postApiCall(API_CONTACT_GROUP_MEMBERS_BULK_DELETE, {
        ids: bulkDelete.ids.map(Number),
        contact_group_id: Number(id),
      })
        .then((res: any) => {
          const parts = [
            res?.deleted && `${res.deleted} deleted`,
            res?.removed && `${res.removed} removed from this group`,
            res?.skipped && `${res.skipped} skipped`,
          ].filter(Boolean);
          toast.success(`Contacts: ${parts.join(', ') || 'nothing changed'}`);
          bulkDelete.clear();
          reload();
        })
        .catch(onDeleteError)
        .finally(() => {
          setBusy(false);
          setShowDeleteModal(false);
          setBulkDelete(null);
        });
      return;
    }
    const url = API_CONTACT_GROUP_MEMBERS_DELETE.replace(':id', contactToDelete);
    setBusy(true);
    postApiCall(url, { id: contactToDelete, contact_group_id: id })
      .then(onDeleteSuccess)
      .catch(onDeleteError)
      .finally(() => {
        setBusy(false);
        setShowDeleteModal(false);
        setContactToDelete(null);
      });
  };

  const confirmDeleteAll = () => {
    setBusy(true);
    postApiCall(API_CONTACT_GROUP_MEMBERS_DELETE_ALL.replace(':id', id), {})
      .then((res: any) => {
        const parts = [
          res?.deleted && `${res.deleted} deleted`,
          res?.removed && `${res.removed} removed from this group`,
        ].filter(Boolean);
        toast.success(`Contacts: ${parts.join(', ') || 'nothing changed'}`);
        setDeleteAllOpen(false);
        reload();
      })
      .catch(onDeleteError)
      .finally(() => setBusy(false));
  };

  function onDeleteSuccess(result) {
    toast.success(
      result?.removed_from_group_only
        ? 'Contact removed from this group'
        : 'Contact deleted successfully',
    );
    setShowToast(true);
    reload();
  }

  function onDeleteError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to delete member');
      setShowToast(true);
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: 'serial',
      header: '#',
      size: 60,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {paging.pageIndex * paging.pageSize + row.index + 1}
        </span>
      ),
    },
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => [row.first_name, row.last_name].filter(Boolean).join(' ') || '-',
    },
    { accessorKey: 'primary_number', header: 'Number' },
    { accessorKey: 'street', header: 'Street Address', cell: ({ row }) => row.original.street || '-' },
    { accessorKey: 'city', header: 'City', cell: ({ row }) => row.original.city || '-' },
    { accessorKey: 'state', header: 'State', cell: ({ row }) => row.original.state || '-' },
    { accessorKey: 'country', header: 'Country', cell: ({ row }) => row.original.country || '-' },
    {
      accessorKey: 'company',
      header: 'Company',
      cell: ({ row }) => row.original.company || '-',
    },
    ...(customUpload.enabled
      ? [
          {
            id: 'custom_template',
            header: 'Custom Field Template',
            accessorFn: templateName,
            cell: ({ row }) => templateName(row.original),
          },
        ]
      : []),
    ...(showCustomFields
      ? [
          {
            id: 'additional_details',
            header: 'Customer Additional Details',
            enableSorting: false,
            accessorFn: (row) => additionalDetailsText(row.additional_details),
            cell: ({ row }) => <AdditionalDetailsCell details={row.original.additional_details} contactName={[row.original.first_name, row.original.last_name].filter(Boolean).join(" ")} />,
          },
        ]
      : []),
    {
      id: 'dates',
      header: 'Created / Updated',
      accessorFn: (row) => row.updated_at || row.created_at || '',
      cell: ({ row }) => datesCell(row.original),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="d-flex align-items-center">
          {/* <RBButton variant="link" className="text-primary p-0 me-2" size="sm">
            <FiPhone size={18} />
          </RBButton> */}
          <Dropdown>
            <Dropdown.Toggle variant="link" className="p-0">
              <FiMoreVertical size={18} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleEditClick(row.original)}>
                <FiEdit className="me-2" /> Edit
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDeleteClick(row.original.id)} className="text-danger">
                <FiTrash2 className="me-2" /> Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Contact Group Members"
        description="Members of this contact group — search, add, edit, or remove."
        actions={
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        }
      />
      <div className="group-detail-container">
      <Row>
        <Col sm="12">
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="px-4 py-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search contacts..."
                    style={{ width: '300px' }}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <div className="d-flex" style={{ gap: '8px' }}>
                    <Button size="sm" variant="outline" onClick={reload} disabled={loading} title="Refresh" aria-label="Refresh">
                      <RefreshCw className={'h-3.5 w-3.5 ' + (loading ? 'animate-spin' : '')} />
                    </Button>
                    {contacts.length > 0 && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'delete') && (
                      <Button size="sm" variant="destructive" onClick={() => setDeleteAllOpen(true)} disabled={busy}>
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete All
                      </Button>
                    )}
                    {contacts.length > 0 && (
                      <Button size="sm" onClick={handleExport} disabled={buttonDisable}>
                        Export
                        {buttonDisable && (
                          <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true" />
                        )}
                      </Button>
                    )}
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" id="dropdown-add-contact">
                        Add Contact
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() =>
                            navigate(`/contact-group/${id}/create-contact`, { state: { group } })
                          }
                        >
                          Create Contact
                        </Dropdown.Item>
                        {canImportContactGroupMembers() && (
                          <>
                            <Dropdown.Item onClick={handleShowUploadModal}>Upload Contact</Dropdown.Item>
                            <Dropdown.Item
                              onClick={() =>
                                navigate(`/contact-group/${id}/import-members`, { state: { group } })
                              }
                            >
                              Import Contact Members
                            </Dropdown.Item>
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    <Modal show={showUploadModal} onHide={() => !busy && handleCloseUploadModal()}>
                      <Modal.Header closeButton>
                        <Modal.Title>Upload Contacts</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <UploadCustomFieldsChooser state={customUpload} disabled={busy} />
                        <p>Download the sample template and upload in same way.</p>
                        <Button
                          className="mb-4"
                          disabled={!customUpload.ready}
                          onClick={(event) => {
                            const { columns: baseColumns, filename, sample_data } = getColumnsAndFilename();
                            const { columns, sample } = customUpload.templateColumns(baseColumns, sample_data);
                            downloadTemplate(
                              event,
                              columns,
                              customUpload.template
                                ? filename.replace('.csv', `-${customUpload.template.name}.csv`)
                                : filename,
                              null,
                              sample,
                            );
                          }}
                        >
                          Download Template
                        </Button>

                        <Form.Control
                          type="file"
                          className="form-control"
                          onChange={(e) => changeUploadFile(e)}
                          accept=".csv"
                          required
                        />

                        <div className="mt-4 rounded-md border border-border bg-muted/40 p-3 text-sm">
                          <p className="mb-2 font-medium">Upload rules</p>
                          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                            <li>Only .csv files. Keep the template's column headers as they are.</li>
                            <li>
                              A contact group can hold at most 50,000 contacts. If the file would take this group
                              past that, nothing is uploaded.
                            </li>
                            <li>
                              Primary Number must be a valid 10-digit number (+91 / leading 0 is removed). Rows with a
                              missing or invalid number are skipped.
                            </li>
                            <li>
                              First Name missing, with control characters (tab, line break), or longer than 255 characters is saved as
                              &quot;Unknown&quot;.
                            </li>
                            <li>
                              Name, Street, City, State, Country and Company accept any characters (including special
                              characters), up to 255 each. Rows with control characters in these are skipped.
                            </li>
                            <li>An invalid Alternate Number is ignored; the contact is still added.</li>
                            <li>
                              A number repeated in the file is added once. A number already in your contacts is
                              not added again: that contact is updated with the file's details (empty cells keep
                              saved values) and added to this group.
                            </li>
                            <li>Skipped rows are listed with their row number after the upload.</li>
                          </ul>
                        </div>
                      </Modal.Body>
                      <Modal.Footer>
                        <Button variant="outline" onClick={handleCloseUploadModal} disabled={busy}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveUpload} disabled={busy || !uploadFile || !customUpload.ready}>
                          {busy ? 'Uploading...' : 'Save'}
                        </Button>
                      </Modal.Footer>
                    </Modal>
                  </div>
                </div>

                <DataTable
                  tableId="contact-group.detail"
                  columns={columns}
                  data={filteredContacts}
                  isLoading={loading}
                  disableSearch
                  emptyTitle={search ? 'No contacts match your search' : 'No contacts in this group yet'}
                  pageSize={paging.pageSize}
                  pageSizeOptions={CONTACT_PAGE_SIZES}
                  serverPagination={{
                    ...paging,
                    rowCount: totalCapped
                      ? Math.max(total, paging.pageIndex * paging.pageSize + contacts.length + (contacts.length === paging.pageSize ? 1 : 0))
                      : total,
                    onChange: (next) =>
                      setPaging(next.pageSize !== paging.pageSize ? { pageIndex: 0, pageSize: next.pageSize } : next),
                  }}
                  disableExport
                  enableRowSelection={hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'delete')}
                  getRowId={(row: any) => String(row.id)}
                  renderSelectionActions={(ids, clear) => (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() => {
                        setBulkDelete({ ids, clear });
                        setShowDeleteModal(true);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete ({ids.length})
                    </Button>
                  )}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showEditModal} onHide={() => !busy && handleModalClose()} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentContact && (
            <Form
              style={{
                maxHeight: 'calc((100vh / var(--app-zoom, 1)) - 250px)',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      First Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={currentContact.first_name}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                      }}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={currentContact.last_name}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>
                  Primary Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="primary_number"
                  value={currentContact.primary_number}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleInputChange({ target: { name: 'primary_number', value: cleanedValue } });
                  }}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                  required
                />
                <Form.Text className="text-muted">
                  {currentContact.primary_number.length === 10
                    ? '✓ Valid 10-digit number'
                    : 'Enter 10 digits (numbers only)'}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Alternate Number 1 (Optional)</Form.Label>
                <Form.Control
                  type="tel"
                  name="alt1_number"
                  value={currentContact.alt1_number}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleInputChange({ target: { name: 'alt1_number', value: cleanedValue } });
                  }}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
                <Form.Text className="text-muted">
                  {currentContact?.alt1_number?.length === 10
                    ? '✓ Valid 10-digit number'
                    : 'Enter 10 digits (numbers only)'}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Alternate Number 2 (Optional)</Form.Label>
                <Form.Control
                  type="tel"
                  name="alt2_number"
                  value={currentContact.alt2_number}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleInputChange({ target: { name: 'alt2_number', value: cleanedValue } });
                  }}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
                <Form.Text className="text-muted">
                  {currentContact?.alt2_number?.length === 10
                    ? '✓ Valid 10-digit number'
                    : 'Enter 10 digits (numbers only)'}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Street Address</Form.Label>
                <Form.Control
                  type="text"
                  name="street"
                  value={currentContact.street}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={currentContact.city}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>State/Province</Form.Label>
                    <Form.Control
                      type="text"
                      name="state"
                      value={currentContact.state}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  type="text"
                  name="country"
                  value={currentContact.country}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                <Form.Control
                  type="text"
                  name="company"
                  value={currentContact.company}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
              </Form.Group>
              {customUpload.enabled && (
                <CustomFieldsSection {...customForm.sectionProps} disabled={busy} />
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={handleModalClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSaveChanges} disabled={busy}>
            {busy ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => {
          if (busy) return;
          setShowDeleteModal(false);
          setBulkDelete(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This removes {bulkDelete ? `${bulkDelete.ids.length} selected contact(s)` : 'the contact'} from
          this group. If the same contact is in other groups, those groups keep it.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline"
            onClick={() => {
              setShowDeleteModal(false);
              setBulkDelete(null);
            }}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirmDelete} disabled={busy}>
            {busy ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog
        open={deleteAllOpen}
        onOpenChange={(open) => !busy && setDeleteAllOpen(open)}
        title="Delete all contacts in this group?"
        description={
          <>
            All <b>{total.toLocaleString()}{totalCapped ? '+' : ''}</b> contact(s) in this group will be removed. A contact that is only in this
            group is deleted; one that is also in another group is only removed from this group. This cannot be undone.
          </>
        }
        confirmLabel={busy ? 'Deleting...' : 'Delete All'}
        tone="destructive"
        loading={busy}
        onConfirm={confirmDeleteAll}
      />
      </div>
    </FillPage>
  );
};

export default GroupDetail;
